import React, { useEffect, useMemo, useRef } from 'react'
import isEqual from 'lodash-es/isEqual'
import { useTranslation } from 'react-i18next'
import {
  Box,
  EmptyState,
  Pagination,
  RowsSkeletonLoader,
  Box as Wrapper,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import FilterBar from 'components/FilterBar/FilterBar'
import { PageHeader } from 'components/header/PageHeader'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useApiPaginationWithPaymentsList } from 'hooks/useApiPaginationWithPaymentsList'
import { useMappedRoles } from 'hooks/useMappedRoles'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { PaymentsResponse, PaymentStatus, PaymentTag } from '../../types/payments'
import SearchFields from './components/Form/SearchFields'
import { CryptoPaymentsHeaderButtons } from './components/Header/CryptoPaymentsHeaderButtons'
import PaymentTable from './components/Table/PaymentTable'
import usePaymentsApi from './hooks/usePaymentsApi'

const Payments = () => {
  const { t } = useTranslation()
  const permissions = useMappedRoles()?.payments

  const canEdit = permissions?.canEdit
  const canCreatePayIn = permissions?.canCreatePayIn
  const isInitialized = useRef(false)

  const {
    executor,
    isLoading,
    isError,
    data: {
      paymentsList: { transactions, paging, pagingMax },
      searchByRef,
      searchByTransactionHash,
      searchByUUID,
      isExportingCSV,
      dateValue,
      initialDateValue,
      merchant,
      status,
      tag,
    },
    actions: {
      onDateSelected,
      handleExportClicked,
      onPaymentStatusChanged,
      onPaymentTagChange,
      onMerchantSelected,
      showSearchResult,
      onResetAll,
      setPaymentsList,
      onPageChange,
      onSearchByRefChanged,
      onSearchByTransactionHash,
      onSearchByUUID,
    },
  } = usePaymentsApi()

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useApiPaginationWithPaymentsList({
      onPageChange,
      setPaymentsList,
      executor,
      pagingMax,
    })

  const urlParams = new URLSearchParams(window.location.search)
  const queryPaymentsLinkBy = urlParams.get('dlt-reference') ?? null

  useEffect(() => {
    return () => {
      onResetAll()
    }
  }, [])

  useEffect(() => {
    if (queryPaymentsLinkBy && !isLoading && isInitialized?.current) {
      onSearchByRefChanged(queryPaymentsLinkBy)
    }
  }, [isInitialized?.current])

  const isUsingFilters = useMemo(() => {
    return (
      !!searchByRef ||
      !!searchByTransactionHash ||
      !!searchByUUID ||
      merchant !== 'ALL' ||
      status !== PaymentStatus.ALL ||
      tag !== PaymentTag.ALL ||
      !isEqual(dateValue, initialDateValue)
    )
  }, [
    searchByRef,
    searchByTransactionHash,
    searchByUUID,
    status,
    tag,
    dateValue,
    initialDateValue,
    merchant,
  ])

  const showNoSearchResults = isUsingFilters && !isLoading && !transactions?.length
  const showHeader = !!transactions?.length || isUsingFilters

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.PAYMENTS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const getPageHeader = () => {
    return t('payments.headerDescriptionTravelRuleV2')
  }

  return (
    <Wrapper className="payments-container">
      {(canEdit || canCreatePayIn) && showHeader && (
        <PageHeader
          description={getPageHeader()}
          renderRightSection={<CryptoPaymentsHeaderButtons refetch={executor} />}
        />
      )}

      {showHeader && (
        <FilterBar
          eventHandlers={{
            executor,
            onMerchantSelected,
            onDateFilterChange: onDateSelected,
            onPaymentStatusChanged,
            handleExportClicked,
            onResetAll,
            showSearchResult,
            onPaymentTagChange,
          }}
          conditions={{ isExportingCSV }}
          filterDateRange={dateValue}
        >
          <SearchFields
            t={t}
            searchByRef={searchByRef}
            searchByTransactionHash={searchByTransactionHash}
            searchByUUID={searchByUUID}
            onSearchByRefChanged={onSearchByRefChanged}
            onSearchByTransactionHash={onSearchByTransactionHash}
            onSearchByUUID={onSearchByUUID}
            queryPaymentsLinkBy={queryPaymentsLinkBy}
          />
        </FilterBar>
      )}

      {isLoading && !isError && (
        <Box paddingT={20}>
          <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
        </Box>
      )}

      {!isLoading && !transactions?.length && !isUsingFilters && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('payments.emptyStateHeadline')}
          description={t('payments.emptyStateCryptoPaymentsDescription')}
          PrimaryButton={
            (canEdit || canCreatePayIn) && <CryptoPaymentsHeaderButtons refetch={executor} />
          }
        />
      )}

      {isError && !isLoading && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('defaultError')}
        />
      )}

      {showNoSearchResults && (
        <NoSearchResults
          testIds={{
            headline: 'payment-links-search-status-card',
            description: 'payment-links-no-result-label',
            clearButton: 'payment-links-clear-result-btn',
          }}
          clearSearchCriteria={onResetAll}
        />
      )}

      {!!transactions?.length && !isError && !isLoading && (
        <PaymentTable
          transactions={transactions as unknown as PaymentsResponse[]}
          isLoading={isLoading}
        />
      )}
      {!isError && !!paging?.total && !isLoading && (
        <Pagination
          testId="payments-pagination"
          page={page}
          rowsPerPage={rowsPerPage}
          total={paging.total}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}
    </Wrapper>
  )
}

export default Payments
